import { useEffect, useState, useCallback, useRef } from "react";
import { api, API, formatApiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/pages/AppLayout";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ClipboardCheck, Check, RotateCcw, Trash2, User, Truck, AlertTriangle, ImagePlus, X, StickyNote } from "lucide-react";

const PRIORITIES = [
  { key: "1day", label: "Within 1 Day", accent: "#FF3B30" },
  { key: "2-3days", label: "2–3 Days", accent: "#FFB800" },
  { key: "5plus", label: "5+ Days", accent: "#3B82F6" },
];

const EMPTY = { title: "", description: "", assignee_id: "", machine_id: "", priority: "1day", due_date: "" };

function resizeImage(file, maxSize = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        if (width >= height) { height = Math.round((height * maxSize) / width); width = maxSize; }
        else { width = Math.round((width * maxSize) / height); height = maxSize; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((b) => b ? resolve(b) : reject(new Error("fail")), "image/jpeg", quality);
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function WorkOrders() {
  const { user, isManager } = useAuth();
  const [orders, setOrders] = useState(null);
  const [members, setMembers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [completing, setCompleting] = useState(null);   // order being completed
  const [compNote, setCompNote] = useState("");
  const [compPhoto, setCompPhoto] = useState(null);
  const [compPreview, setCompPreview] = useState(null);
  const [compBusy, setCompBusy] = useState(false);
  const [compHours, setCompHours] = useState("");
  const [compMins, setCompMins] = useState("");
  const [repairType, setRepairType] = useState("in_house");
  const [outsourcedTo, setOutsourcedTo] = useState("");
  const compFileRef = useRef(null);

  const load = useCallback(() => { api.get("/work-orders").then(({ data }) => setOrders(data)); }, []);
  useEffect(() => {
    load();
    if (isManager) {
      api.get("/fleet/members").then(({ data }) => setMembers(data));
      api.get("/machines").then(({ data }) => setMachines(data));
    }
  }, [load, isManager]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.assignee_id) { toast.error("Choose a staff member to assign."); return; }
    if (!form.machine_id) { toast.error("Select a machine (by fleet number) for this work order."); return; }
    try {
      await api.post("/work-orders", {
        title: form.title, description: form.description, assignee_id: form.assignee_id,
        machine_id: form.machine_id, priority: form.priority,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
      });
      toast.success("Work order assigned");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/work-orders/${id}`, { status }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const openComplete = (o) => { setCompleting(o); setCompNote(""); setCompPhoto(null); setCompPreview(null); setCompHours(""); setCompMins(""); setRepairType("in_house"); setOutsourcedTo(""); };

  const pickCompPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image."); return; }
    try { const b = await resizeImage(file); setCompPhoto(b); setCompPreview(URL.createObjectURL(b)); }
    catch { toast.error("Could not process image."); }
  };

  const submitComplete = async () => {
    if (!completing) return;
    setCompBusy(true);
    try {
      const totalMins = (parseInt(compHours || "0", 10) || 0) * 60 + (parseInt(compMins || "0", 10) || 0);
      await api.patch(`/work-orders/${completing.id}`, {
        status: "done", completion_note: compNote,
        time_spent_minutes: totalMins,
        repair_type: repairType,
        outsourced_to: repairType === "outsourced" ? outsourcedTo : "",
      });
      if (compPhoto) {
        const fd = new FormData();
        fd.append("file", compPhoto, "done.jpg");
        await api.post(`/work-orders/${completing.id}/completion-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Marked done");
      setCompleting(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setCompBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/work-orders/${id}`); toast.success("Work order deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="p-5 md:p-10 pb-24" data-testid="work-orders-page">
      <PageHead title="Work Orders" sub={isManager ? "Assign jobs to your team by priority." : "Jobs assigned to you, by priority."}>
        {isManager && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="btn-signal h-12 px-5 font-head flex items-center gap-2" data-testid="new-work-order-btn"><Plus className="w-4 h-4" /> New Work Order</button>
            </DialogTrigger>
            <DialogContent className="bg-[#1A1C23] border border-white/10 text-white max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-head uppercase text-2xl">New Work Order</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4" data-testid="work-order-form">
                <div><label className="il-label">Title *</label><input className="il-input" value={form.title} onChange={set("title")} required placeholder="e.g. Replace hydraulic hose" data-testid="wo-title" /></div>
                <div><label className="il-label">Details</label><textarea className="il-input py-3" style={{ height: "auto", minHeight: "5rem" }} value={form.description} onChange={set("description")} placeholder="What needs doing?" data-testid="wo-description" /></div>
                <div><label className="il-label">Assign To *</label>
                  <select className="il-input" value={form.assignee_id} onChange={set("assignee_id")} required data-testid="wo-assignee">
                    <option value="">Select staff member…</option>
                    {members.map((mb) => <option key={mb.id} value={mb.id}>{mb.name} ({mb.role})</option>)}
                  </select>
                </div>
                <div><label className="il-label">Machine (by Fleet No.) *</label>
                  <select className="il-input" value={form.machine_id} onChange={set("machine_id")} required data-testid="wo-machine">
                    <option value="">Select a machine…</option>
                    {machines.map((mc) => <option key={mc.id} value={mc.id}>{mc.fleet_number ? `${mc.fleet_number} — ` : ""}{mc.make} {mc.model}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="il-label">Priority *</label>
                    <select className="il-input" value={form.priority} onChange={set("priority")} data-testid="wo-priority">
                      {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                  </div>
                  <div><label className="il-label">Due Date</label><input className="il-input font-mono" type="date" value={form.due_date} onChange={set("due_date")} data-testid="wo-due" /></div>
                </div>
                <DialogFooter><button className="btn-signal w-full h-12 font-head" data-testid="wo-submit">Assign Work Order</button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </PageHead>

      {orders === null ? (
        <p className="font-head text-[#FFB800] uppercase tracking-widest">Loading…</p>
      ) : orders.length === 0 ? (
        <div className="card p-12 text-center" data-testid="wo-empty">
          <ClipboardCheck className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
          <p className="font-body text-[#94A3B8]">{isManager ? "No work orders yet. Create one to assign a job." : "No work orders assigned to you."}</p>
        </div>
      ) : (
        <div className="space-y-8">
          {PRIORITIES.map((p) => {
            const now = Date.now();
            const list = orders
              .filter((o) => o.priority === p.key)
              .sort((a, b) => {
                if (!a.due_date && !b.due_date) return 0;
                if (!a.due_date) return 1;
                if (!b.due_date) return -1;
                return new Date(a.due_date) - new Date(b.due_date);
              });
            if (list.length === 0) return null;
            return (
              <div key={p.key} data-testid={`wo-group-${p.key}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-3 h-3" style={{ background: p.accent }} />
                  <h2 className="font-head font-900 uppercase tracking-tight text-xl">{p.label}</h2>
                  <span className="font-mono text-xs text-[#94A3B8]">({list.length})</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {list.map((o) => {
                    const isOverdue = o.due_date && o.status !== "done" && new Date(o.due_date).getTime() < now;
                    return (
                    <div key={o.id} className={`card p-5 ${o.status === "done" ? "opacity-60" : ""} ${isOverdue ? "border-[#FF3B30]/60" : ""}`} data-testid={`wo-card-${o.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-head font-800 text-lg uppercase tracking-tight leading-tight">{o.title}</p>
                        <span className={`badge ${o.status === "done" ? "badge-healthy" : o.status === "in_progress" ? "badge-upcoming" : "badge-overdue"}`} data-testid={`wo-status-${o.id}`}>{o.status.replace("_", " ")}</span>
                      </div>
                      {o.description && <p className="font-body text-sm text-[#94A3B8] mt-2">{o.description}</p>}
                      <div className="mt-3 space-y-1 font-mono text-xs text-[#94A3B8]">
                        <p className="flex items-center gap-2"><User className="w-3.5 h-3.5" /> {o.assignee_name}</p>
                        {o.machine_name && <p className="flex items-center gap-2"><Truck className="w-3.5 h-3.5" /> {o.machine_name}</p>}
                        {o.due_date && (
                          isOverdue
                            ? <p className="flex items-center gap-1.5 text-[#FF3B30] font-700" data-testid={`wo-overdue-${o.id}`}><AlertTriangle className="w-3.5 h-3.5" /> OVERDUE · due {fmtDate(o.due_date)}</p>
                            : <p>Due {fmtDate(o.due_date)}</p>
                        )}
                      </div>
                      {o.has_fault_photo && (
                        <div className="mt-3 border-t border-white/10 pt-3" data-testid={`wo-fault-${o.id}`}>
                          <p className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30] flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3 h-3" /> Pre-start fault photo</p>
                          <img src={`${API}/work-orders/${o.id}/fault-photo?token=${localStorage.getItem("ironlog_token")}`} alt="Reported fault" className="w-full max-h-40 object-cover border border-white/10" data-testid={`wo-fault-photo-${o.id}`} />
                        </div>
                      )}
                      {o.status === "done" && (o.completion_note || o.has_completion_photo || o.time_spent_minutes != null || o.repair_type) && (
                        <div className="mt-3 border-t border-white/10 pt-3" data-testid={`wo-completion-${o.id}`}>
                          <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] flex items-center gap-1.5 mb-1"><StickyNote className="w-3 h-3" /> Completed{o.completed_by_name ? ` by ${o.completed_by_name}` : ""}</p>
                          {o.completion_note && <p className="font-body text-sm text-white/90">{o.completion_note}</p>}
                          {(o.time_spent_minutes != null || o.repair_type) && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {o.time_spent_minutes != null && <span className="badge border border-white/15 text-[#94A3B8]" data-testid={`wo-time-${o.id}`}>{Math.floor(o.time_spent_minutes / 60)}h {o.time_spent_minutes % 60}m</span>}
                              {o.repair_type && <span className={`badge ${o.repair_type === "outsourced" ? "badge-upcoming" : "badge-healthy"}`} data-testid={`wo-repairtype-${o.id}`}>{o.repair_type === "outsourced" ? `Outsourced${o.outsourced_to ? ` · ${o.outsourced_to}` : ""}` : "In-house"}</span>}
                            </div>
                          )}
                          {o.has_completion_photo && (
                            <img src={`${API}/work-orders/${o.id}/completion-photo?token=${localStorage.getItem("ironlog_token")}`} alt="Completed work" className="mt-2 w-full max-h-40 object-cover border border-white/10" data-testid={`wo-completion-photo-${o.id}`} />
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-4">
                        {o.status !== "done" ? (
                          <button onClick={() => openComplete(o)} className="btn-signal h-9 px-3 font-head text-xs flex items-center gap-1.5 flex-1 justify-center" data-testid={`wo-done-${o.id}`}><Check className="w-3.5 h-3.5" /> Mark Done</button>
                        ) : (
                          <button onClick={() => setStatus(o.id, "open")} className="btn-ghost h-9 px-3 font-head text-xs flex items-center gap-1.5 flex-1 justify-center" data-testid={`wo-reopen-${o.id}`}><RotateCcw className="w-3.5 h-3.5" /> Reopen</button>
                        )}
                        {isManager && (
                          <button onClick={() => remove(o.id)} className="h-9 px-3 border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors flex items-center" data-testid={`wo-delete-${o.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Complete work order dialog */}
      <Dialog open={!!completing} onOpenChange={(v) => !v && setCompleting(null)}>
        <DialogContent className="bg-[#1A1C23] border border-white/10 text-white" data-testid="wo-complete-dialog">
          <DialogHeader><DialogTitle className="font-head uppercase text-2xl">Complete Job</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="font-body text-sm text-[#94A3B8]">{completing?.title}</p>
            <div><label className="il-label">What did you do? (optional)</label>
              <textarea className="il-input py-3" style={{ height: "auto", minHeight: "5rem" }} value={compNote} onChange={(e) => setCompNote(e.target.value)} placeholder="e.g. Replaced hydraulic hose, topped up oil" data-testid="wo-complete-note" /></div>
            <div>
              <label className="il-label">Time spent</label>
              <div className="flex gap-2">
                <div className="flex-1"><input type="number" min="0" className="il-input font-mono" value={compHours} onChange={(e) => setCompHours(e.target.value)} placeholder="Hours" data-testid="wo-complete-hours" /></div>
                <div className="flex-1"><input type="number" min="0" max="59" className="il-input font-mono" value={compMins} onChange={(e) => setCompMins(e.target.value)} placeholder="Minutes" data-testid="wo-complete-mins" /></div>
              </div>
            </div>
            <div>
              <label className="il-label">Repair type</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setRepairType("in_house")} className={`badge ${repairType === "in_house" ? "badge-healthy" : "border border-white/15 text-[#94A3B8]"}`} data-testid="wo-repair-inhouse">In-house</button>
                <button type="button" onClick={() => setRepairType("outsourced")} className={`badge ${repairType === "outsourced" ? "badge-upcoming" : "border border-white/15 text-[#94A3B8]"}`} data-testid="wo-repair-outsourced">Outsourced (3rd party)</button>
              </div>
              {repairType === "outsourced" && (
                <input className="il-input mt-2" value={outsourcedTo} onChange={(e) => setOutsourcedTo(e.target.value)} placeholder="Who did the work? (e.g. Dave's Diesel)" data-testid="wo-outsourced-to" />
              )}
            </div>
            <div>
              <label className="il-label">Photo (optional)</label>
              {compPreview ? (
                <div className="relative w-40">
                  <img src={compPreview} alt="Proof" className="w-40 h-40 object-cover border border-white/10" data-testid="wo-complete-preview" />
                  <button type="button" onClick={() => { setCompPhoto(null); setCompPreview(null); }} className="absolute top-1 right-1 bg-[#0D0E11]/80 border border-white/20 p-1 hover:bg-[#FF3B30]/80 transition-colors"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <button type="button" onClick={() => compFileRef.current?.click()} className="w-40 h-40 border-2 border-dashed border-white/15 flex flex-col items-center justify-center gap-1 text-[#94A3B8] hover:border-[#FFB800]/50 hover:text-[#FFB800] transition-colors" data-testid="wo-complete-photo-btn"><ImagePlus className="w-6 h-6" /><span className="font-body text-[11px]">Add photo</span></button>
              )}
              <input ref={compFileRef} type="file" accept="image/*" capture="environment" onChange={pickCompPhoto} className="hidden" data-testid="wo-complete-photo-input" />
            </div>
          </div>
          <DialogFooter><button onClick={submitComplete} disabled={compBusy} className="btn-signal w-full h-12 font-head" data-testid="wo-complete-submit">{compBusy ? "Saving…" : "Mark as Done"}</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
